import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

const WorkspaceContext = createContext(null);
export const useWorkspace = () => useContext(WorkspaceContext);

function applyTheme(ws) {
  if (!ws?.theme) return;
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", ws.theme.primary || "#064E3B");
  root.style.setProperty("--brand-accent", ws.theme.accent || "#D4AF37");
}

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [current, setCurrent] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const cfg = (await api.get("/config")).data;
      const list = (await api.get("/workspaces")).data;
      setWorkspaces(list);
      const saved = localStorage.getItem("workspace_id");
      const active = list.find((w) => w.id === saved) || list[0];
      if (active) {
        localStorage.setItem("workspace_id", active.id);
        setCurrent(active);
        applyTheme(active);
        try {
          const pricing = (await api.get("/pricing")).data;
          setConfig({ ...cfg, ...pricing });
        } catch {
          setConfig(cfg);
        }
      } else {
        setConfig(cfg);
      }
    })();
  }, [user]);

  const refreshPricing = async () => {
    const pricing = (await api.get("/pricing")).data;
    setConfig((prev) => ({ ...(prev || {}), ...pricing }));
  };

  const switchWorkspace = (ws) => {
    localStorage.setItem("workspace_id", ws.id);
    setCurrent(ws);
    applyTheme(ws);
    window.location.reload();
  };

  const reloadWorkspace = async () => {
    const { data } = await api.get("/workspaces");
    setWorkspaces(data);
    const active = data.find((w) => w.id === current?.id) || data[0];
    setCurrent(active);
    applyTheme(active);
  };

  return (
    <WorkspaceContext.Provider value={{ workspaces, current, config, switchWorkspace, reloadWorkspace, refreshPricing }}>
      {current ? (
        children
      ) : (
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      )}
    </WorkspaceContext.Provider>
  );
}
